import VoiceSettingsModal from "@/components/modal/VoiceSettingsModal";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { updateElementProps } from "@/store/slideEditorSlice";
import { type AudioPlayerElement, MediaUsageContextSchema } from "@ai-teach/types";
import { DEFAULT_VOICE } from "@ai-teach/utils";
import { useEffect, useState } from "react";
import { FaVolumeUp } from "react-icons/fa";

interface AudioElementToolbarProps {
  audioElement: AudioPlayerElement;
}

/**
 * The narration behind the clip, and the button that speaks it. The script is what the lesson
 * generator writes, so this is where an author reads what was said and says it differently.
 * Local state + commit-on-blur mirrors CompositionElementToolbar, so typing doesn't churn undo.
 */
export function AudioElementToolbar({ audioElement }: AudioElementToolbarProps) {
  const dispatch = useAppDispatch();
  const { slide } = useAppSelector((state) => state.slideEditor.present);
  const [localScript, setLocalScript] = useState(audioElement.script ?? "");
  const [isVoiceSettingsOpen, setIsVoiceSettingsOpen] = useState(false);

  useEffect(() => {
    setLocalScript(audioElement.script ?? "");
  }, [audioElement.script]);

  const commitScript = (value: string) => {
    dispatch(updateElementProps({ id: audioElement.id, props: { script: value } }));
  };

  return (
    <div className="flex items-center gap-1.5">
      <label htmlFor="audio-script" className="text-xs font-semibold text-gray-500 uppercase">
        Script
      </label>
      <textarea
        id="audio-script"
        rows={2}
        value={localScript}
        onChange={(e) => setLocalScript(e.target.value)}
        onBlur={(e) => commitScript(e.target.value)}
        placeholder="What the learner hears"
        className="w-72 px-2 py-1 text-sm border text-gray-800 border-gray-300 rounded-md resize-none focus:outline-none focus:ring-2 focus:ring-blue-400"
      />
      <button
        type="button"
        onClick={() => setIsVoiceSettingsOpen(true)}
        disabled={localScript.trim() === ""}
        aria-label="Generate audio from script"
        title="Generate audio from script 產生音檔"
        className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-600 disabled:opacity-40"
      >
        <FaVolumeUp className="w-3.5 h-3.5" />
      </button>

      {isVoiceSettingsOpen && slide && (
        <VoiceSettingsModal
          audioData={{
            id: audioElement.media?.id ?? null,
            filename: audioElement.media?.filename ?? null,
            src: audioElement.media?.src ?? null,
          }}
          onClose={() => setIsVoiceSettingsOpen(false)}
          text={localScript.trim()}
          slideId={slide.id}
          voice={DEFAULT_VOICE}
          usageContext={MediaUsageContextSchema.Values.aiTextToSpeech}
          onSave={({ src, id, filename }) => {
            dispatch(
              updateElementProps({
                id: audioElement.id,
                props: { script: localScript.trim(), media: { src, id, filename } },
              })
            );
            setIsVoiceSettingsOpen(false);
          }}
        />
      )}
    </div>
  );
}
